import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useDebounce from 'react-use/lib/useDebounce';

import {
  CircularProgress,
  Grid,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  Value,
} from '@material-ui/lab';

import isArray from 'lodash/isArray';
import isString from 'lodash/isString';

import { useCurrentUserGroups, useIdentitySearch } from '../../hooks';
import { IdentityKind, identityKindToFilter } from '../../utils';
import { IdentityChip } from '../IdentityChip';
import { IdentityItem } from '../IdentityItem';

// Helper type for autocomplete value based on multiple and disableClearable props
type IdentityAutocompleteValue<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
> = Multiple extends true
  ? string[]
  : DisableClearable extends true
    ? string
    : string | null;

export type IdentityAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
> = Omit<
  TextFieldProps,
  'onChange' | 'value' | 'options' | 'renderOption' | 'renderTags' | 'error'
> & {
  value?: IdentityAutocompleteValue<Multiple, DisableClearable>;
  onChange?: (
    event: ChangeEvent<{}>,
    value: IdentityAutocompleteValue<Multiple, DisableClearable>,
    reason: string,
    details?: AutocompleteChangeDetails<string>,
  ) => void;
  multiple?: Multiple;
  disableClearable?: DisableClearable;
  disabledOptions?: string[];
  loading?: boolean;
  error?: boolean | string;
  allowedKinds?: readonly IdentityKind[];
};

export function IdentityAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  disabledOptions = [],
  value,
  onChange,
  multiple,
  disableClearable,
  loading: externalLoading,
  disabled,
  label,
  placeholder,
  required,
  variant = 'outlined',
  margin = 'dense',
  helperText,
  error,
  allowedKinds,
  ...rest
}: IdentityAutocompleteProps<Multiple, DisableClearable>) {
  const { identities: groupIdentities = [], isLoading: userGroupsLoading } =
    useCurrentUserGroups();

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useDebounce(
    () => {
      setDebouncedQuery(searchQuery);
    },
    300,
    [searchQuery],
  );

  const identityKinds = allowedKinds?.map(k => identityKindToFilter[k]);

  const { identities, isLoading, hasNextPage, nextPage, isLoadingNextPage } =
    useIdentitySearch({
      query: debouncedQuery || '*',
      identityKinds,
      skip: disabled,
    });

  // Always fetch selected values to ensure they're visible in the dropdown
  const selectedValueFilter = useMemo(() => {
    const selectedNames: string[] = [];
    if (multiple && Array.isArray(value)) {
      selectedNames.push(...value.filter(Boolean));
    } else if (value && typeof value === 'string') {
      selectedNames.push(value);
    }

    if (selectedNames.length === 0) return undefined;

    return `name:(${selectedNames.map(name => `"${name}"`).join(' OR ')})`;
  }, [value, multiple]);

  // Fetch selected values in parallel to ensure they're visible when dropdown opens
  const { identities: selectedIdentities } = useIdentitySearch({
    query: '*',
    identityKinds,
    filter: selectedValueFilter,
    skip: disabled || !selectedValueFilter,
  });

  const shouldIncludeGroups = allowedKinds?.includes('group') ?? true;

  const optionGroupLabel = useMemo(() => {
    if (allowedKinds?.length === 1 && allowedKinds[0] === 'group') {
      return 'All groups';
    }
    return 'All users and groups';
  }, [allowedKinds]);

  // Combine identities from both queries, with groups first
  const allIdentities = useMemo(() => {
    const combined: typeof identities = [];

    if (shouldIncludeGroups && groupIdentities.length > 0) {
      const query = debouncedQuery?.toLowerCase();
      const matchingGroups = groupIdentities.filter(g => {
        if (query && query !== '*') {
          return g.displayName.toLowerCase().includes(query);
        }
        return true;
      });

      combined.push(...matchingGroups);
    }

    identities
      .filter(identity => !combined.some(i => i.name === identity.name))
      .forEach(identity => combined.push(identity));

    // Only prioritize selected identities when not actively searching
    if (!debouncedQuery || debouncedQuery === '*') {
      selectedIdentities.forEach(identity => {
        const existingIndex = combined.findIndex(i => i.name === identity.name);
        if (existingIndex !== -1) {
          combined.splice(existingIndex, 1);
        }
        combined.unshift(identity);
      });
    }

    return combined;
  }, [
    identities,
    selectedIdentities,
    debouncedQuery,
    shouldIncludeGroups,
    groupIdentities,
  ]);

  // Sync inputValue with selected value's displayName (only when not actively searching)
  useEffect(() => {
    // Don't interfere with user's typing - only sync when not searching
    if (searchQuery) return;

    if (!multiple && value && typeof value === 'string') {
      const identity = allIdentities.find(i => i.name === value);
      if (identity?.displayName) {
        setInputValue(identity.displayName);
      }
    } else if (!multiple && !value) {
      setInputValue('');
    }
  }, [value, allIdentities, multiple, searchQuery, setInputValue]);

  const sanitizedValue = useMemo(() => {
    if (multiple) {
      const arrayValue = isArray(value) ? value : [];
      return arrayValue as IdentityAutocompleteValue<
        Multiple,
        DisableClearable
      >;
    }

    const singleValue = isString(value) ? value : null;
    return singleValue as IdentityAutocompleteValue<Multiple, DisableClearable>;
  }, [value, multiple]);

  // Build options list from identities
  const options = useMemo(() => {
    return allIdentities.map(identity => identity.name);
  }, [allIdentities]);

  // Build identity lookup map
  const identityMap = useMemo(() => {
    return new Map(allIdentities.map(identity => [identity.name, identity]));
  }, [allIdentities]);

  const groupIdentityNames = useMemo(
    () => new Set(groupIdentities.map(g => g.name)),
    [groupIdentities],
  );

  const getOptionGroup = useCallback(
    (option: string) =>
      groupIdentityNames.has(option) ? 'Your groups' : optionGroupLabel,
    [groupIdentityNames, optionGroupLabel],
  );

  const loading = isLoading || userGroupsLoading || externalLoading;
  const isLoadingSearch = debouncedQuery !== searchQuery || isLoading;

  // Handle error prop - can be boolean or string
  const hasError = typeof error === 'string' ? error.length > 0 : !!error;
  const errorHelperText = typeof error === 'string' ? error : helperText;

  // Determine if there are no results after filtering
  const hasNoResults = allIdentities.length === 0 && !isLoading;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Autocomplete<string, Multiple, DisableClearable, false>
          fullWidth
          options={options}
          groupBy={
            shouldIncludeGroups && groupIdentityNames.size > 0
              ? getOptionGroup
              : undefined
          }
          value={
            sanitizedValue as Value<string, Multiple, DisableClearable, false>
          }
          onChange={(_event, newValue, reason) => {
            // Update inputValue and clear search query when selection is made
            if (reason === 'select-option') {
              if (multiple) {
                setInputValue('');
                setSearchQuery('');
              } else if (newValue) {
                const identity = allIdentities.find(i => i.name === newValue);
                setInputValue(identity?.displayName || '');
                setSearchQuery('');
              }
            } else if (reason === 'clear' || reason === 'remove-option') {
              setInputValue('');
              setSearchQuery('');
            }

            onChange?.(
              {} as ChangeEvent<{}>,
              newValue as IdentityAutocompleteValue<Multiple, DisableClearable>,
              reason as 'select-option' | 'remove-option' | 'clear',
            );
          }}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            // Update both inputValue and search query when user is typing
            if (reason === 'input') {
              setInputValue(newInputValue);
              setSearchQuery(newInputValue);
            } else if (reason === 'clear') {
              setInputValue('');
              setSearchQuery('');
            }
          }}
          filterOptions={x => x}
          disableListWrap
          openOnFocus
          autoHighlight
          multiple={multiple}
          disableClearable={disableClearable}
          disabled={disabled}
          loading={loading}
          getOptionDisabled={option => disabledOptions.some(d => d === option)}
          getOptionLabel={option => {
            const identity = identityMap.get(option);
            return identity?.displayName || option;
          }}
          renderOption={option => {
            const identity = identityMap.get(option);
            return identity ? <IdentityItem identity={identity} /> : option;
          }}
          renderTags={(tagOptions, getTagProps) =>
            tagOptions.map((option, index) => {
              const identity = identityMap.get(option);
              if (!identity) return null;
              return (
                <IdentityChip
                  key={option}
                  identityRef={identity}
                  {...getTagProps({ index })}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              {...rest}
              label={label}
              placeholder={placeholder}
              required={required}
              variant={variant}
              margin={margin}
              helperText={errorHelperText}
              error={hasError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingSearch && (
                      <CircularProgress size="1em" color="inherit" />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={hasNoResults ? 'No identities found' : 'No options'}
          ListboxProps={{
            style: { maxHeight: '300px' },
            onScroll: (event: SyntheticEvent) => {
              const listboxNode = event.currentTarget;
              const { scrollHeight, scrollTop, clientHeight } = listboxNode;

              const endIsVisible =
                scrollHeight - scrollTop - clientHeight <= 10;
              if (
                endIsVisible &&
                hasNextPage &&
                !isLoading &&
                !isLoadingNextPage
              ) {
                nextPage();
              }
            },
          }}
        />
      </Grid>
    </Grid>
  );
}
